import React from 'react';
import { Landmark, UserCheck, CreditCard, ExternalLink } from 'lucide-react';
import { PublicSection, Reveal } from '@/components/public';
import { STRIPE_URL, STRIPE_LOGO } from '@/config/integrations';

const DETAILS = [
  {
    icon: Landmark,
    title: 'Direct Bank Payouts',
    text: 'All online food orders paid via credit or debit card are processed securely through Stripe and transferred directly to your Canadian business bank account.',
  },
  {
    icon: UserCheck,
    title: 'Quick Onboarding Requirement',
    text: 'Every restaurant merchant connects or creates a Stripe account during Hungry Canadian setup.',
  },
  {
    icon: CreditCard,
    title: 'Canadian Card Support',
    text: 'Native processing for Visa, Mastercard, American Express, and digital wallets (Apple Pay & Google Pay).',
  },
];

const WALLETS = ['Visa', 'Mastercard', 'Amex', 'Interac Online', 'Apple Pay', 'Google Pay'];

export default function StripeSection() {
  return (
    <PublicSection
      id="stripe-payments"
      testId="stripe-section"
      variant="default"
      eyebrow="Payment & Payout Engine"
      title="Automated Payments & Merchant Payouts Powered by Stripe"
      description="Stripe Connect is the payment backbone of every Hungry Canadian storefront — no invoicing, no waiting, no manual reconciliation."
    >
      <div className="grid gap-10 lg:grid-cols-5 lg:gap-14 items-start">
        <div className="lg:col-span-3">
          <Reveal>
            <span
              data-testid="stripe-mandatory-badge"
              className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-primary text-primary-foreground text-[11px] md:text-xs font-bold uppercase tracking-wide"
            >
              Mandatory Core Connection
            </span>
          </Reveal>

          <div className="mt-7 space-y-6">
            {DETAILS.map(({ icon: Icon, title, text }, i) => (
              <Reveal key={title} delay={i * 0.06}>
                <div className="flex items-start gap-4" data-testid={`stripe-detail-${i}`}>
                  <span className="mt-0.5 inline-flex w-10 h-10 shrink-0 items-center justify-center bg-primary/10 text-primary">
                    <Icon className="w-5 h-5" strokeWidth={2.25} />
                  </span>
                  <div>
                    <h3 className="text-base md:text-lg font-heading font-bold text-foreground">{title}</h3>
                    <p className="mt-1 text-sm md:text-base text-muted-foreground leading-relaxed">{text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.2}>
            <a
              href={STRIPE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="stripe-learn-more-cta"
              className="mt-9 inline-flex items-center gap-2 h-12 px-7 rounded-full bg-primary text-primary-foreground font-semibold hover:bg-primary-hover transition-[background-color,transform] duration-200 hover:-translate-y-0.5"
            >
              Learn More About Stripe <ExternalLink className="w-4 h-4" />
            </a>
          </Reveal>
        </div>

        {/* Visual payout card */}
        <Reveal delay={0.1} className="lg:col-span-2">
          <div className="bg-card border border-border p-6 md:p-7" data-testid="stripe-visual-card">
            <img src={STRIPE_LOGO} alt="Stripe" className="h-7 mb-6" />
            <div className="space-y-3">
              {[
                { label: 'Customer pays online', value: '$42.80 CAD' },
                { label: 'Stripe processes securely', value: 'Visa •••• 4242' },
                { label: 'Payout to your bank', value: 'Direct deposit' },
              ].map((row, i) => (
                <div key={row.label}>
                  <div className="flex items-center justify-between bg-background border border-border px-4 py-3">
                    <span className="text-sm text-muted-foreground">{row.label}</span>
                    <span className="text-sm font-semibold text-foreground">{row.value}</span>
                  </div>
                  {i < 2 && <div aria-hidden className="mx-auto w-px h-3 bg-border" />}
                </div>
              ))}
            </div>
            <div className="mt-6 pt-5 border-t border-border">
              <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground mb-3">
                Accepted in Canada
              </p>
              <div className="flex flex-wrap gap-2">
                {WALLETS.map((w) => (
                  <span
                    key={w}
                    className="px-2.5 py-1 border border-border bg-background text-xs font-medium text-foreground/80"
                  >
                    {w}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </PublicSection>
  );
}
